/**
 * LYT Team Redux Operations (Thunks)
 * Async operations for LYT internal team data
 */

import * as actions from './lyt-team.actions';
import { getLytTeamMembers, removeLytMembership } from '../../services/requests/users-and-permissions/users/users';
import { sendLytInvitations } from '../../services/requests/users-and-permissions/invitations';
import { showErrorToast, showSuccessToast } from '../../components/toast';
import { normalizeTeamMembers } from '../../data/Team/team-normalizers';

/** Default page size for the LYT team-members endpoint */
const DEFAULT_LYT_TEAM_MEMBERS_LIMIT = 10;

/**
 * Fetch LYT internal team members (users + invites) with server-side pagination, sorting, and filtering.
 *
 * All filtering / sorting is done by the backend.
 * The response contains a mixed list where each item has an `is_prospect` boolean.
 *
 * @param {Object} [params] - Query parameters (merged with stored params in Redux)
 * @param {string}  [params.searchTerm]  - Free-text search across name / email
 * @param {number}  [params.page=1]      - 1-based page number
 * @param {number}  [params.limit]       - Rows per page (falls back to stored / default)
 * @param {string}  [params.sortBy]      - 'name' | 'role' | 'last_active'
 * @param {string}  [params.sortOrder]   - 'asc' | 'desc'
 * @param {string}  [params.roleFilter]  - Exact role value, e.g. 'lyt_admin'
 * @returns {Function} Redux thunk
 */
export const getLytTeamMembersOperation = (params = {}) => async (dispatch, getState) => {
    // Merge incoming params over whatever is already stored in Redux
    const storedParams = getState().lytTeam?.members?.params || {};
    const merged = { ...storedParams, ...params };

    const {
        searchTerm,
        page = 1,
        limit = DEFAULT_LYT_TEAM_MEMBERS_LIMIT,
        sortBy,
        sortOrder,
        roleFilter,
    } = merged;

    // Persist the resolved params so the UI can read them back
    dispatch(actions.setLytTeamMembersParams({ searchTerm, page, limit, sortBy, sortOrder, roleFilter }));
    dispatch(actions.getLytTeamMembersRequest());

    // Compute zero-based offset from 1-based page number
    const offset = (page - 1) * limit;

    try {
        const response = await getLytTeamMembers({
            searchTerm: searchTerm || undefined,
            offset,
            limit,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
            roleFilter: roleFilter || undefined,
        });

        // axios wraps in response.data → { pagination, data }
        const responseData = response?.data;
        const rawData = responseData?.data || [];
        const pagination = responseData?.pagination || { offset: 0, limit, count: 0 };

        // Normalise raw API objects into consistent model shapes
        const data = normalizeTeamMembers(rawData);

        dispatch(actions.getLytTeamMembersSuccess({ data, pagination }));
        return { data, pagination };
    } catch (error) {
        dispatch(actions.getLytTeamMembersError(error));
        dispatch(showErrorToast(
            'Failed to load LYT team members',
            'Something went wrong while loading the LYT team. Please try again.',
        ));
        throw error;
    }
};

/**
 * Reset LYT team members state (on unmount)
 * @returns {Function} Redux thunk
 */
export const resetLytTeamMembersOperation = () => (dispatch) => {
    dispatch(actions.resetLytTeamMembers());
};

/**
 * Send invitations for LYT internal team members.
 *
 * Calls POST /invitations WITHOUT agency_id (LYT users don't belong to a customer agency).
 * On success, shows a toast and refreshes the LYT team list.
 *
 * @param {Object} payload
 * @param {string[]} payload.recipients        - Email addresses to invite
 * @param {string}   payload.assigned_role     - Role to assign (e.g. 'lyt_admin')
 * @param {string}   [payload.customized_message] - Optional personal message
 * @returns {Function} Redux thunk that resolves with { success, data } or { success, validationError, data }
 */
export const sendLytInvitationsOperation = (payload) => async (dispatch) => {
    try {
        const response = await sendLytInvitations(payload);

        const successCount = response?.successfulInvitationsProcessCount ?? 0;

        if (successCount > 0) {
            dispatch(showSuccessToast(
                `${successCount} user(s) invited successfully.`,
            ));
            // Refresh LYT team members list (not the agency team list)
            dispatch(getLytTeamMembersOperation({ page: 1 }));
        }

        return { success: true, data: response };
    } catch (error) {
        // 422 — the backend could not create the invitation(s), typically
        // because the invitee already has an account or a pending invitation.
        // Keep the modal open so the user can correct the email list.
        const isAlreadyExistsError = error?.statusCode === 422;

        if (isAlreadyExistsError) {
            return { success: false, validationError: true, alreadyExists: true, data: error };
        }

        // Check for validation error (400 — email validation, etc.)
        const isValidationError = error?.statusCode === 400 || error?.error === 'Bad Request';

        if (isValidationError) {
            return { success: false, validationError: true, data: error };
        }

        dispatch(showErrorToast(
            'Failed to send invitations',
            'Something went wrong. Please try again.',
        ));

        return { success: false, validationError: false, data: error };
    }
};

/**
 * Remove a user's membership from the LYT internal team and refresh the list.
 *
 * Calls DELETE /users/lyt/remove-membership with the user's email and account
 * creation timestamp. On success a toast is shown and the LYT team members
 * list is re-fetched so the removed user disappears.
 *
 * @param {Object} payload
 * @param {string} payload.user_email    - Email of the user to remove
 * @param {string} payload.created_at_ts - ISO timestamp of account creation
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const removeLytMembershipOperation = (payload) => async (dispatch) => {
    try {
        const response = await removeLytMembership(payload);

        dispatch(showSuccessToast('User removed successfully.'));

        // Refresh the LYT team members list so the removed user disappears
        dispatch(getLytTeamMembersOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to remove user',
            'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};
